import { motion } from "framer-motion";
import { MessageSquare, Code, Rocket, Users } from "lucide-react";

const HowItWorks = () => {
  const steps = [
    {
      icon: MessageSquare,
      number: "01",
      title: "Describe Your App",
      description: "Tell Joylo what you want to build in plain English. Our AI understands your vision and translates it into a technical specification.",
    },
    {
      icon: Code,
      number: "02",
      title: "AI Builds Your App",
      description: "Watch as Joylo's AI generates production-ready code, sets up your database, and creates a beautiful interface - all in minutes.",
    },
    {
      icon: Users,
      number: "03",
      title: "Backed by Joylo Architects",
      description: "Every build is backed by certified Joylo engineers — on demand, in one click. They take full responsibility for your app's success.",
    },
    {
      icon: Rocket,
      number: "04",
      title: "Deploy Anywhere",
      description: "Launch your app to production with one click, or download the code and deploy to your own infrastructure. You own everything.",
    }
  ];

  return (
    <section id="how-it-works" className="py-10 sm:py-20 bg-gradient-to-b from-white to-warm-cream-50">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-left sm:text-center mb-8 sm:mb-16"
        >
          <div className="inline-flex items-center gap-2 glass px-3 sm:px-4 py-2 rounded-full mb-5 sm:mb-6 border border-warm-cream-300">
            <span className="text-xs sm:text-sm font-medium text-soft-lavender-600">How It Works</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-ink-blue-900 mb-4 sm:mb-6 leading-[1.15]">
            From Idea to Production in 4 Simple Steps
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-soft-gray-600 max-w-3xl sm:mx-auto leading-relaxed">
            Building production-grade applications has never been easier. Here's how Joylo transforms your ideas into reality.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group bg-white rounded-2xl p-6 sm:p-8 border border-warm-cream-200 hover:border-warm-cream-300 hover:shadow-elegant-xl transition-all duration-300"
            >
              <div className="flex items-start gap-4 sm:gap-6">
                {/* Subtle Icon */}
                <div className="flex-shrink-0">
                  <step.icon className="h-7 w-7 sm:h-8 sm:w-8 text-soft-lavender-500 group-hover:text-sunset-coral-500 transition-colors duration-300" />
                  {/* Subtle number indicator */}
                  <span className="text-xs font-medium text-soft-gray-400 tracking-widest uppercase mt-2 block">
                    {step.number}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-ink-blue-900 mb-2 sm:mb-4 leading-[1.2]">
                    {step.title}
                  </h3>
                  <p className="text-sm sm:text-base text-soft-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>

              {/* Subtle hover indicator */}
              <div className="mt-4 sm:mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="h-1 w-12 bg-gradient-sunset rounded-full"></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-8 sm:mt-16"
        >
          <a
            href="https://joylo.io"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-sunset text-white font-semibold text-base sm:text-lg rounded-xl hover:shadow-elegant-lg transition-all duration-200"
          >
            Start Building Now
            <Rocket className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;